/**
 * Phase 18.4 — Approved-sketch admin panel.
 *
 * Surfaces the customer-confirmed Design sketch on the admin
 * estimate-review page so the admin can see the canonical layout the
 * customer agreed to BEFORE finalizing the offer. Renders the same SVG
 * the customer saw (via `DesignSketch`) plus an approval timestamp and
 * a disclosure that lists prior superseded iterations on demand.
 *
 * No actions live here — admins consume the sketch read-only;
 * approve/reject is a customer-only action surfaced in the chat.
 */

import { useEffect, useState } from "react";

import { listDesigns } from "@starz/shared/api/sdk.gen";
import type { Design } from "@starz/shared/api/types.gen";

import { DesignSketch } from "@/components/chat/DesignSketch";

interface Props {
  estimateId: string;
}

export function ApprovedSketchPanel({ estimateId }: Props) {
  const [designs, setDesigns] = useState<Design[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await listDesigns({ estimateId });
      if (cancelled || "error" in res) return;
      setDesigns(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId]);

  if (designs === null) return null;

  const approved = designs.find(
    (d) => d.approved_at != null && d.superseded_at == null,
  );
  const priorIterations = designs.filter((d) => d.design_id !== approved?.design_id);

  // Don't render the panel when there's nothing approved AND no
  // history — the admin doesn't need an empty card cluttering the
  // workspace column.
  if (!approved && priorIterations.length === 0) return null;

  return (
    <section className="rounded-xl border border-starz-gold/20 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Approved sketch
      </h3>
      {approved ? (
        <div>
          <DesignSketch design={approved} kind="design_approved" />
          <p className="mt-2 text-[11px] italic text-warm-gray">
            Approved {formatTimestamp(approved.approved_at!)} by customer.
          </p>
        </div>
      ) : (
        <p className="text-sm text-warm-gray">
          The customer hasn't approved any sketch yet.
        </p>
      )}

      {priorIterations.length > 0 ? (
        <details
          open={showHistory}
          onToggle={(e) => setShowHistory((e.target as HTMLDetailsElement).open)}
          className="mt-4 border-t border-starz-gold/10 pt-3"
        >
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-widest text-warm-gray">
            Show all sketches ({priorIterations.length} prior)
          </summary>
          <div className="mt-3 space-y-4">
            {priorIterations.map((d) => (
              <div key={d.design_id} className="border-t border-starz-gold/10 pt-3">
                <DesignSketch design={d} />
                <p className="mt-1 text-[11px] italic text-warm-gray">
                  Drawn {formatTimestamp(d.computed_at)}
                  {d.superseded_at
                    ? ` · superseded ${formatTimestamp(d.superseded_at)}`
                    : ""}
                </p>
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </section>
  );
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}
